import { Bell, CheckCheck, X } from "lucide-react"
import type { CSSProperties, RefObject } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { useMarkAllRead, useMarkRead, useNotifications } from "../hooks"
import { notificationLink } from "../links"

import { NotificationCard } from "./NotificationCard"

/**
 * Popover width in px. Exported so the bell can hand it to `useAnchoredPanel`
 * for horizontal viewport clamping; the `max-w` class below shrinks it further
 * on phone-width screens.
 */
export const NOTIFICATION_PANEL_WIDTH = 360
/**
 * 480px notification list + header/footer chrome. Keeps the bell menu roomy
 * without changing the compact default used by selects and pickers.
 */
export const NOTIFICATION_PANEL_MAX_HEIGHT = 560

interface Props {
  /**
   * Positioning produced by `useAnchoredPanel` on the bell: `position: fixed`
   * offsets plus the measured `maxHeight`.
   */
  style: CSSProperties
  popoverRef: RefObject<HTMLDivElement>
  onClose: () => void
}

/**
 * PRD-26 §FR-11 — the bell's popover. Shows the last 10 non-archived
 * notifications, one mark-all-read action, one "Ver todas" link.
 * Click any row → marks read + navigates via the SPA-computed link.
 *
 * Portals to document.body so the popover is immune to any overflow:hidden or
 * position:relative ancestor on the sidebar that might clip it. Positioning,
 * dismissal and the height clamp come from the bell's `useAnchoredPanel`.
 */
export function NotificationDropdown({ style, popoverRef, onClose }: Props) {
  const { t } = useTranslation("notifications")
  const navigate = useNavigate()
  const { data: notifications = [], isLoading } = useNotifications({ limit: 10 })
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  const unreadCount = notifications.filter((n) => !n.read).length

  const handleRowClick = (notifId: string, target: string, alreadyRead: boolean) => {
    if (!alreadyRead) {
      markRead.mutate(notifId)
    }
    onClose()
    navigate(target)
  }

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={t("dropdown.aria")}
      style={{ ...style, width: NOTIFICATION_PANEL_WIDTH }}
      // `max-w` keeps the popover inside a 375px viewport; `overflow-y-auto`
      // makes the hook's measured `maxHeight` scroll instead of clipping the
      // last notifications past the viewport edge.
      className="rounded-card border-border bg-surface shadow-float z-50 max-w-[calc(100vw-1rem)] overflow-y-auto overscroll-contain border"
    >
      <header className="border-hairline flex items-center justify-between border-b px-3 py-2.5">
        <h3 className="text-ink inline-flex items-center gap-1.5 text-sm font-semibold">
          <Bell className="text-accent h-4 w-4" aria-hidden />
          {t("dropdown.title")}
        </h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 ? (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              className="rounded-control text-accent-ink hover:bg-accent-soft inline-flex min-h-11 items-center gap-1 px-2 text-xs font-medium disabled:opacity-60 md:min-h-8"
            >
              <CheckCheck className="h-3 w-3" aria-hidden />
              {t("dropdown.mark_all_read")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            aria-label={t("dropdown.close")}
            className="rounded-control text-ink-subtle hover:bg-inset hover:text-ink flex h-11 w-11 items-center justify-center md:h-8 md:w-8"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </header>

      <div className="max-h-[480px] overflow-y-auto p-2">
        {isLoading ? (
          <div className="text-meta flex items-center justify-center py-12">
            {t("dropdown.loading")}
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-2">
            {notifications.map((n) => {
              const target = notificationLink(n)
              return (
                <li key={n.id}>
                  <NotificationCard
                    notification={n}
                    onClick={() => handleRowClick(n.id, target, n.read)}
                    compact
                  />
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <footer className="border-hairline border-t px-3 py-2 text-right">
        <button
          type="button"
          onClick={() => {
            onClose()
            navigate("/notificaciones")
          }}
          className="rounded-control text-accent-ink hover:bg-accent-soft inline-flex min-h-11 items-center gap-1 px-2 text-xs font-medium md:min-h-8"
        >
          {t("dropdown.view_all")} →
        </button>
      </footer>
    </div>,
    document.body,
  )
}

function EmptyState() {
  const { t } = useTranslation("notifications")
  return (
    <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
      <Bell className="text-ink-subtle h-8 w-8" aria-hidden />
      <p className="text-ink mt-3 text-sm font-medium">{t("empty.all.title")}</p>
      <p className="text-meta mt-1">{t("empty.all.body")}</p>
    </div>
  )
}
